// packages/shared/api-client/endpoints/posts.js
// News feed / posts API endpoints

import { baseApi } from "../baseApi";

/**
 * Posts API endpoints
 * Provides news feed, posts, comments, and reactions
 */
const postApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get all posts (news feed)
    getPosts: builder.query({
      query: (params = {}) => ({
        url: '/api/newsFeed',
        params, // { page, limit, sort, pinned, authorId, search, category }
      }),
      providesTags: (result) =>
        result?.data?.posts
          ? [
              ...result.data.posts.map(({ id }) => ({ type: 'Post', id })),
              { type: 'Post', id: 'LIST' },
            ]
          : [{ type: 'Post', id: 'LIST' }],
    }),

    // Get specific post by ID
    getPostById: builder.query({
      query: (id) => `/api/newsFeed/${id}`,
      providesTags: (result, error, id) => [{ type: 'Post', id }],
    }),

    // Get post categories
    getPostCategories: builder.query({
      query: () => '/api/newsFeed/categories',
      providesTags: ['PostCategory'],
    }),

    // Create new post
    createPost: builder.mutation({
      query: (postData) => ({
        url: '/api/newsFeed',
        method: 'POST',
        body: postData,
      }),
      invalidatesTags: [{ type: 'Post', id: 'LIST' }],
    }),

    // Update post
    updatePost: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/newsFeed/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Post', id },
        { type: 'Post', id: 'LIST' },
      ],
    }),

    // Delete post
    deletePost: builder.mutation({
      query: (id) => ({
        url: `/api/newsFeed/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Post', id: 'LIST' }],
    }),

    // Pin/unpin post
    togglePinPost: builder.mutation({
      query: ({ id, pinned }) => ({
        url: `/api/newsFeed/${id}/pin`,
        method: 'PATCH',
        body: { pinned },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Post', id },
        { type: 'Post', id: 'LIST' },
      ],
    }),

    // Add comment to post
    addComment: builder.mutation({
      query: ({ postId, content }) => ({
        url: `/api/newsFeed/${postId}/comments`,
        method: 'POST',
        body: { content },
      }),
      invalidatesTags: (_result, _error, { postId }) => [
        { type: 'Post', id: postId },
        { type: 'Comment', id: 'LIST' },
      ],
    }),

    // Delete comment
    deleteComment: builder.mutation({
      query: ({ postId, commentId }) => ({
        url: `/api/newsFeed/${postId}/comments/${commentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { postId }) => [
        { type: 'Post', id: postId },
        { type: 'Comment', id: 'LIST' },
      ],
    }),

    // Add reaction to post
    addReaction: builder.mutation({
      query: ({ postId, reactionType }) => ({
        url: `/api/newsFeed/${postId}/reactions`,
        method: 'POST',
        body: { reactionType }, // 'like', 'love', etc.
      }),
      invalidatesTags: (_result, _error, { postId }) => [{ type: 'Post', id: postId }],
    }),

    // Remove reaction from post
    removeReaction: builder.mutation({
      query: (postId) => ({
        url: `/api/newsFeed/${postId}/reactions`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, postId) => [{ type: 'Post', id: postId }],
    }),

    // Track post view
    trackPostView: builder.mutation({
      query: (postId) => ({
        url: `/api/newsFeed/${postId}/view`,
        method: 'POST',
      }),
      // Don't invalidate cache - views are tracked passively
    }),

    // Get post performance (admin only)
    getPostPerformance: builder.query({
      query: ({ page = 1, limit = 50, sortBy = 'createdAt', sortOrder = 'desc', category } = {}) => {
        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
          sortBy,
          sortOrder,
        });
        if (category) params.append('category', category);

        return {
          url: `/api/newsFeed/performance?${params.toString()}`,
        };
      },
      providesTags: ['PostPerformance'],
    }),

    // Get post analytics (admin only)
    getPostAnalytics: builder.query({
      query: (postId) => `/api/newsFeed/${postId}/analytics`,
      providesTags: (result, error, postId) => [{ type: 'PostAnalytics', id: postId }],
    }),
  }),
});

// Export hooks for usage in components
export const useGetPostsQuery = postApi.useGetPostsQuery;
export const useLazyGetPostsQuery = postApi.useLazyGetPostsQuery;
export const useGetPostByIdQuery = postApi.useGetPostByIdQuery;
export const useGetPostCategoriesQuery = postApi.useGetPostCategoriesQuery;
export const useGetPostPerformanceQuery = postApi.useGetPostPerformanceQuery;
export const useGetPostAnalyticsQuery = postApi.useGetPostAnalyticsQuery;
export const useCreatePostMutation = postApi.useCreatePostMutation;
export const useUpdatePostMutation = postApi.useUpdatePostMutation;
export const useDeletePostMutation = postApi.useDeletePostMutation;
export const useTogglePinPostMutation = postApi.useTogglePinPostMutation;
export const useAddCommentMutation = postApi.useAddCommentMutation;
export const useDeleteCommentMutation = postApi.useDeleteCommentMutation;
export const useAddReactionMutation = postApi.useAddReactionMutation;
export const useRemoveReactionMutation = postApi.useRemoveReactionMutation;
export const useTrackPostViewMutation = postApi.useTrackPostViewMutation;
